import React, { useState } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { reviewDocument } from '@/store/slices/workAuthSlice';
import { useToast } from '@/hooks/useToast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Calendar, CheckCircle, XCircle, AlertCircle, Download, ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import DocumentViewer from './DocumentViewer';
import type { DocumentResponse, DocumentReviewStatus } from '@shared/schemas/workAuth';
import { formatDateTime } from '@/utils/dateFormatter';

interface DocumentReviewModalProps {
  document: DocumentResponse | null;
  isOpen: boolean;
  onClose: () => void;
  onReviewComplete?: () => void;
}

const DocumentReviewModal: React.FC<DocumentReviewModalProps> = ({
  document,
  isOpen,
  onClose,
  onReviewComplete
}) => {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const [reviewStatus, setReviewStatus] = useState<DocumentReviewStatus>('APPROVED');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [expandedPdf, setExpandedPdf] = useState(false);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      setPresignedUrl(null);
      setReviewStatus('APPROVED' as any);
      setReviewNotes('');
      setExpandedPdf(false);
      setLoadingUrl(false);
    }
  }, [isOpen]);

  // Fetch presigned URL when modal opens with a new document
  React.useEffect(() => {
    if (!isOpen || !document) return;

    let isMounted = true;

    const fetchPresignedUrl = async () => {
      setLoadingUrl(true);
      try {
        const { workAuthService } = await import('@/services/workAuthService');
        const url = await workAuthService.getDocumentPresignedUrl(document.id);

        if (isMounted) {
          setPresignedUrl(url);
        }
      } catch (error) {
        if (isMounted) {
          alert('Failed to load document: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
      } finally {
        if (isMounted) {
          setLoadingUrl(false);
        }
      }
    };

    fetchPresignedUrl();

    return () => {
      isMounted = false;
    };
  }, [isOpen, document?.id]);

  if (!document) return null;

  const handleSubmitReview = async () => {
    // Validation
    if ((reviewStatus === 'REJECTED' || reviewStatus === 'RESUBMISSION_REQUIRED') && !reviewNotes) {
      toast.error('Validation Error', 'Please provide review notes for rejection or resubmission request');
      return;
    }

    setSubmitting(true);
    try {
      const result = await dispatch(reviewDocument({
        documentId: document.id,
        reviewData: {
          reviewStatus,
          reviewNotes: reviewNotes || undefined
        }
      })).unwrap();

      // Reset form
      setReviewStatus('APPROVED' as any);
      setReviewNotes('');

      toast.success('Review Submitted', 'Document review has been submitted successfully');
      onReviewComplete?.();
      onClose();
    } catch (error) {
      let errorMessage = 'Failed to submit review. Please try again.';

      if (typeof error === 'string') {
        // rejectWithValue() returns a string
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as any).message;
      }

      toast.error('Review Failed', errorMessage);
      console.error('Review submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getReviewStatusColor = (status: DocumentReviewStatus) => {
    switch (status) {
      case 'APPROVED':
        return 'text-green-600';
      case 'REJECTED':
        return 'text-red-600';
      case 'PENDING':
        return 'text-yellow-600';
      case 'RESUBMISSION_REQUIRED':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  const getReviewStatusIcon = (status: DocumentReviewStatus) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className={`h-5 w-5 ${getReviewStatusColor(status)}`} />;
      case 'REJECTED':
        return <XCircle className={`h-5 w-5 ${getReviewStatusColor(status)}`} />;
      case 'PENDING':
        return <AlertCircle className={`h-5 w-5 ${getReviewStatusColor(status)}`} />;
      default:
        return <FileText className={`h-5 w-5 ${getReviewStatusColor(status)}`} />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${expandedPdf ? 'max-w-full max-h-[95vh]' : 'max-w-5xl max-h-[90vh]'} overflow-hidden flex flex-col`}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {document?.reviewStatus === 'PENDING' ? 'Review Document' : 'Document Preview'}
          </DialogTitle>
          <DialogDescription>
            {document?.reviewStatus === 'PENDING'
              ? 'Review and approve or reject the submitted work authorization document'
              : 'View the submitted work authorization document'}
          </DialogDescription>
        </DialogHeader>

        <div className={`flex-1 overflow-y-auto ${expandedPdf ? 'flex gap-6' : ''}`}>
          {expandedPdf ? (
            <>
              {/* Expanded PDF View - Full Width on Left */}
              <div className="flex-1 flex flex-col min-w-0 border border-border rounded-lg bg-background">
                <div className="flex items-center justify-between p-3 border-b border-border bg-slate-50 dark:bg-slate-950">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <p className="font-medium truncate text-foreground">{document.fileName}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" asChild disabled={!presignedUrl} className="text-foreground hover:bg-slate-100 dark:hover:bg-slate-800">
                      <a href={presignedUrl ? `${presignedUrl}?download=true` : '#'} download={document.fileName} rel="noopener noreferrer">
                        <Download className="h-4 w-4" />
                      </a>
                    </Button>
                    <Button variant="ghost" size="sm" asChild disabled={!presignedUrl} className="text-foreground hover:bg-slate-100 dark:hover:bg-slate-800">
                      <a href={presignedUrl || '#'} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-hidden p-4 bg-white dark:bg-slate-900 flex flex-col">
                  {presignedUrl ? (
                    <DocumentViewer
                      key={`${document.id}-expanded`}
                      filePath={presignedUrl}
                      fileName={document.fileName}
                      maxHeight="100%"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Loading document...
                    </div>
                  )}
                </div>
              </div>

              {/* Review Form on Right - Scrollable */}
              <div className="w-80 flex flex-col border border-border rounded-lg bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 overflow-hidden">
                <div className="p-4 border-b border-border bg-slate-100 dark:bg-slate-800">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExpandedPdf(false)}
                    className="w-full justify-center gap-2"
                  >
                    <Minimize2 className="h-4 w-4" />
                    Collapse
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {document?.reviewStatus === 'PENDING' ? (
                    <>
                      {/* Review Form Content */}
                      <div className="space-y-2">
                    <Label>Review Decision</Label>
                    <Select value={reviewStatus} onValueChange={(val) => setReviewStatus(val as DocumentReviewStatus)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="APPROVED">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Approve
                          </div>
                        </SelectItem>
                        <SelectItem value="REJECTED">
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            Reject
                          </div>
                        </SelectItem>
                        <SelectItem value="RESUBMISSION_REQUIRED">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                            Request Resubmission
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rejection / Resubmission */}
                  <div className="space-y-2">
                    <Label htmlFor="reviewNotes">
                      Notes
                      {(reviewStatus === 'REJECTED' || reviewStatus === 'RESUBMISSION_REQUIRED') && (
                        <span className="text-red-600"> *</span>
                      )}
                    </Label>
                    <Textarea
                      id="reviewNotes"
                      placeholder={reviewStatus === 'REJECTED' || reviewStatus === 'RESUBMISSION_REQUIRED'
                        ? "Explain why this document is being rejected or what needs to be corrected..."
                        : "Add any notes about this review decision (optional)..."}
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      rows={4}
                      required={reviewStatus === 'REJECTED' || reviewStatus === 'RESUBMISSION_REQUIRED'}
                    />
                    <p className="text-xs text-muted-foreground">
                      These notes will be sent to the manager in an email notification
                    </p>
                  </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">This document has already been reviewed and cannot be modified in preview mode.</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Normal View - Stacked Layout */}
              <div className="space-y-6">
                {/* Document Information */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 p-4 rounded-lg space-y-3 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <p className="font-medium text-lg text-foreground">{document.fileName}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedPdf(true)}
                      className="gap-2"
                    >
                      <Maximize2 className="h-4 w-4" />
                      Expand
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Document Type</p>
                      <p className="font-medium">{document.documentType}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Current Status</p>
                      <div className="flex items-center gap-2 mt-1">
                        {getReviewStatusIcon(document.reviewStatus)}
                        <p className="font-medium capitalize">
                          {document.reviewStatus.replace(/_/g, ' ').toLowerCase()}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Uploaded</p>
                      <p className="font-medium">
                        {formatDateTime(document.submittedAt)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Submitted By</p>
                      <p className="font-medium">{document.submittedBy}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">File Size</p>
                      <p className="font-medium">
                        {document.fileSize ? `${(document.fileSize / 1024).toFixed(2)} KB` : 'N/A'}
                      </p>
                    </div>
                    {document.reviewedAt && (
                      <div>
                        <p className="text-muted-foreground">Reviewed On</p>
                        <p className="font-medium">
                          {formatDateTime(document.reviewedAt)}
                        </p>
                      </div>
                    )}
                    {document.reviewer && (
                      <div>
                        <p className="text-muted-foreground">Reviewed By</p>
                        <p className="font-medium">{document.reviewer.firstName} {document.reviewer.lastName}</p>
                      </div>
                    )}
                    {document.reviewNotes && (
                      <div className="col-span-2">
                        <p className="text-muted-foreground">Review Notes</p>
                        <p className="font-medium text-sm">{document.reviewNotes}</p>
                      </div>
                    )}
                  </div>

                  {/* Download Buttons */}
                  <div className="pt-3 border-t flex gap-2">
                    <Button variant="outline" size="sm" asChild disabled={!presignedUrl}>
                      <a href={presignedUrl ? `${presignedUrl}?download=true` : '#'} download={document.fileName} rel="noopener noreferrer">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild disabled={!presignedUrl}>
                      <a href={presignedUrl || '#'} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open in New Tab
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Document Preview */}
                <div className="border border-border rounded-lg bg-white dark:bg-slate-900 shadow-sm overflow-hidden flex flex-col" style={{ maxHeight: '400px', padding: 0 }}>
                  {presignedUrl ? (
                    <DocumentViewer
                      key={`${document.id}-normal`}
                      filePath={presignedUrl}
                      fileName={document.fileName}
                      maxHeight="400px"
                    />
                  ) : (
                    <div className="flex items-center justify-center py-8 text-muted-foreground">
                      Loading document...
                    </div>
                  )}
                </div>

                {document.reviewStatus !== 'PENDING' && (
                  <div className="text-center py-4 bg-muted/30 rounded p-3 text-sm">
                    <p className="text-muted-foreground">This document has already been reviewed and cannot be modified in preview mode.</p>
                  </div>
                )}

                {/* Review Form */}
                {document.reviewStatus === 'PENDING' ? (
                  <div className="space-y-4 border-t border-border pt-4 bg-gradient-to-b from-slate-50 to-transparent dark:from-slate-900 dark:to-transparent p-4">
                    <div className="space-y-2">
                      <Label>Review Decision</Label>
                    <Select value={reviewStatus} onValueChange={(val) => setReviewStatus(val as DocumentReviewStatus)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="APPROVED">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Approve
                          </div>
                        </SelectItem>
                        <SelectItem value="REJECTED">
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            Reject
                          </div>
                        </SelectItem>
                        <SelectItem value="RESUBMISSION_REQUIRED">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                            Request Resubmission
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Review Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="reviewNotes">
                      Notes
                      {(reviewStatus === 'REJECTED' || reviewStatus === 'RESUBMISSION_REQUIRED') && (
                        <span className="text-red-600"> *</span>
                      )}
                    </Label>
                    <Textarea
                      id="reviewNotes"
                      placeholder={reviewStatus === 'REJECTED' || reviewStatus === 'RESUBMISSION_REQUIRED'
                        ? "Explain why this document is being rejected or what needs to be corrected..."
                        : "Add any notes about this review decision (optional)..."}
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      rows={4}
                      required={reviewStatus === 'REJECTED' || reviewStatus === 'RESUBMISSION_REQUIRED'}
                    />
                    <p className="text-xs text-muted-foreground">
                      These notes will be sent to the manager in an email notification
                    </p>
                  </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {document?.reviewStatus === 'PENDING' ? 'Cancel' : 'Close'}
          </Button>
          {document?.reviewStatus === 'PENDING' && (
            <Button onClick={handleSubmitReview} disabled={submitting}>
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>Submit Review</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentReviewModal;
